import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Trash2, Loader2, Check, Route as RouteIcon } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";

export default function CareerPaths() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", area: "", from_level: "", to_level: "", stepsText: "" });

  const load = () => api.get("/px/career").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.title.trim()) return toast.error("El título es obligatorio");
    const steps = f.stepsText.split("\n").map((s) => s.trim()).filter(Boolean);
    try { await api.post("/px/career", { title: f.title, area: f.area, from_level: f.from_level, to_level: f.to_level, steps }); setOpen(false); setF({ title: "", area: "", from_level: "", to_level: "", stepsText: "" }); load(); toast.success("Ruta creada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggle = async (c, s) => { try { const r = await api.post(`/px/career/${c.id}/toggle/${s.id}`); setItems((prev) => prev.map((x) => x.id === c.id ? { ...x, steps: r.data.steps, progress: r.data.progress } : x)); } catch { /* */ } };
  const del = async (c) => { try { await api.delete(`/px/career/${c.id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA Career Paths">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="career-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="overline text-navy/40 mb-2">OMNIA People Suite · Talent Management</div>
            <h2 className="font-editorial text-4xl tracking-tight">OMNIA Career Paths</h2>
            <p className="mt-2 text-navy/50">Diseña rutas de carrera con pasos de desarrollo y da seguimiento al avance.</p>
          </div>
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="career-new"><Plus className="w-4 h-4" /> Nueva ruta</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin rutas de carrera.</div> :
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((c) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5" data-testid={`career-${c.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <RouteIcon className="w-4 h-4 text-violet" />
                  <div className="min-w-0"><div className="font-medium truncate">{c.title}</div><div className="text-[11px] text-navy/40">{c.area}{c.from_level && c.to_level ? ` · ${c.from_level} → ${c.to_level}` : ""}</div></div>
                  <button onClick={() => del(c)} className="ml-auto text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2 mb-3"><div className="flex-1 h-2 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-violet transition-all" style={{ width: `${c.progress}%` }} /></div><span className="text-xs text-navy/50 w-9 text-right">{c.progress}%</span></div>
                <div className="space-y-1.5">
                  {c.steps.map((s) => (
                    <button key={s.id} onClick={() => toggle(c, s)} className="w-full flex items-center gap-2 text-left text-sm py-1 group" data-testid={`career-step-${s.id}`}>
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${s.done ? "bg-violet border-violet text-white" : "border-navy/20 group-hover:border-violet"}`}>{s.done && <Check className="w-3 h-3" />}</span>
                      <span className={s.done ? "line-through text-navy/40" : "text-navy/75"}>{s.label}</span>
                    </button>
                  ))}
                  {c.steps.length === 0 && <div className="text-xs text-navy/40">Sin pasos definidos.</div>}
                </div>
              </motion.div>
            ))}
          </div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="career-dialog">
          <div className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">Nueva ruta de carrera</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5 text-navy/40" /></button></div>
            <div className="space-y-3">
              <input className={input} placeholder="Título (ej. De Analista a Gerente)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="career-title" />
              <input className={input} placeholder="Área / Familia de puestos" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input className={input} placeholder="Nivel inicial" value={f.from_level} onChange={(e) => setF({ ...f, from_level: e.target.value })} />
                <input className={input} placeholder="Nivel destino" value={f.to_level} onChange={(e) => setF({ ...f, to_level: e.target.value })} />
              </div>
              <textarea className={input} rows={4} placeholder="Pasos de desarrollo (uno por línea)" value={f.stepsText} onChange={(e) => setF({ ...f, stepsText: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="career-submit">Crear</button></div>
          </div>
        </div>
      )}
    </OSLayout>
  );
}
